import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/common/Button";
import {
  Wind,
  Heart,
  Paintbrush,
  Star,
  Users,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";
import styles from "./HomePage.module.css";

const VIDEO_SRC =
  // "https://pub-1905c682d2e248d9a047ebb1dd594491.r2.dev/IMG_5743.MOV";
  "https://pub-1905c682d2e248d9a047ebb1dd594491.r2.dev/Video_2.MOV";

export function HomePage() {
  const navigate = useNavigate();

  /* ── Video player state ── */
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => {
      if (v.duration) setProgress((v.currentTime / v.duration) * 100);
    };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", onTime);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", onTime);
    };
  }, []);

  const revealControls = () => {
    setShowControls(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!videoRef.current?.paused) setShowControls(false);
    }, 2500);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v) return;
    const pct = Number(e.target.value);
    if (isFinite(v.duration)) v.currentTime = (pct / 100) * v.duration;
    setProgress(pct);
  };

  const paintingBenefits = [
    {
      icon: Wind,
      title: "A Mindful Escape",
      description: "Slow down, disconnect from daily distractions, and enjoy a peaceful creative moment.",
    },
    {
      icon: Paintbrush,
      title: "Creative Confidence",
      description: "Create beautiful artwork regardless of your artistic experience.",
    },
    {
      icon: Star,
      title: "A Sense of Achievement",
      description: "Experience the satisfaction of transforming a blank canvas into finished art.",
    },
    {
      icon: Users,
      title: "Meaningful Moments",
      description: "Turn quiet time, family gatherings, or time with friends into lasting memories.",
    },
    {
      icon: Heart,
      title: "Relaxing & Rewarding",
      description: "Enjoy a creative activity that feels both calming and fulfilling.",
    },
  ];


  return (
    <div className={styles.homePage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <img
          src="/website cover.jpg.jpeg"
          alt="Artsy Icile"
          className={styles.heroCoverImage}
        />
      </section>

      {/* Left brush — sits exactly on the hero ↔ video border */}
      <div className={styles.brushAnchorLeft} aria-hidden="true">
        <img src="/brush_1.png" className={styles.brushLeft} alt="" />
      </div>

      {/* Video Section */}
      <section className={styles.videoSection}>
        {/* <p className={styles.videoLabel}>Watch our story</p> */}
        <div
          className={styles.videoWrapper}
          onMouseMove={revealControls}
          onMouseLeave={() => playing && setShowControls(false)}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            className={styles.video}
            src={VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
          />

          {/* Custom controls overlay */}
          <div
            className={`${styles.videoControls} ${showControls || !playing ? styles.videoControlsVisible : ""}`}
          >
            {/* Progress bar */}
            <div
              className={styles.progressTrack}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.progressFill}
                style={{ width: `${progress}%` }}
              />
              <input
                type="range"
                min={0}
                max={100}
                step={0.1}
                value={progress}
                onChange={handleSeek}
                className={styles.progressRange}
              />
            </div>

            {/* Buttons */}
            <div className={styles.controlsRow}>
              <button
                className={styles.controlBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause size={20} /> : <Play size={20} />}
              </button>
              <button
                className={styles.controlBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMute();
                }}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            </div>
          </div>

          {/* Big play icon when paused */}
          {!playing && (
            <div className={styles.bigPlayBtn}>
              <Play size={48} />
            </div>
          )}
        </div>
      </section>

      {/* Benefits of Paint by Numbers */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <h2 className={styles.benefitsSectionTitle}>
            The <em className={styles.scriptText}>benefits</em> of Paint by
            Numbers
          </h2>
          <div className={styles.benefitsGrid}>
            {paintingBenefits.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className={styles.benefitItem}>
                  <div className={styles.benefitIcon}>
                    <Icon size={38} strokeWidth={1.4} />
                  </div>
                  <h3 className={styles.benefitTitle}>{item.title}</h3>
                  <p className={styles.benefitDescription}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Right brush — sits exactly on the benefits ↔ art-therapy border */}
      <div className={styles.brushAnchorRight} aria-hidden="true">
        <img src="/brush_2.png" className={styles.brushRight} alt="" />
      </div>

      {/* Art Therapy Section */}
      <section className={styles.artTherapySection}>
        <img
          src="/website cover pic2.jpg.jpeg"
          alt="Art Therapy"
          className={styles.artTherapyCoverImage}
        />
      </section>

      {/* Why Artsyicile Section */}
      <section className={styles.whySection}>
        <div className={styles.container}>
          <h2 className={styles.whySectionTitle}>
            Why <em className={styles.scriptText}>Artsyicile</em> ?
          </h2>
          <p className={styles.whyParagraph}>
            Originality is at the heart of everything we create. From the first sketch to the final product, every
            detail is thoughtfully designed to tell unique stories and invite you to become part of the creative
            process. We create experiences that feel personal, inspiring, and truly one of a kind.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className={styles.cta}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to Start Your Creative Journey?</h2>
            <p className={styles.ctaDescription}>
              Join us for a relaxing and inspiring paint by numbers workshop
            </p>
            <Button
              onClick={() => navigate("/products/workshops#top")}
              variant="primary"
            >
              Book a Workshop
            </Button>
          </div>
        </div>
      </section> */}
    </div>
  );
}
